import React, { useState } from "react";
import { graphql, Link } from "gatsby";

import {
    analysisPage,
    pageShell,
    intro,
    introCopy,
    eyebrow,
    introTitle,
    introLead,
    introBadges,
    introBadge,
    introArt,
    quizSection,
    progressMeta,
    progressTrack,
    progressFill,
    questionCard,
    questionNumber,
    questionTitle,
    questionHint,
    questionVisual,
    answersGrid,
    answerButton,
    answerSelected,
    quizNav,
    backButton,
    privacyNote,
    resultSection,
    resultHeader,
    resultEyebrow,
    resultTitle,
    confidence,
    resultGrid,
    resultPortrait,
    resultSummary,
    resultPalette,
    swatch,
    resultLists,
    resultList,
    avoidList,
    scoreDetails,
    scoreRow,
    scoreBar,
    resultActions,
    primaryAction,
    secondaryAction,
    restartButton,
    disclaimer,
} from "./color-analysis.module.scss";

import heroImage from "../../assets/images/beauty/color-analysis-hero.jpg";
import springImage from "../../assets/images/beauty/color-analysis-result-spring.jpg";
import summerImage from "../../assets/images/beauty/reference/beauty-summer-reference.jpg";
import autumnImage from "../../assets/images/beauty/color-analysis-result-autumn.jpg";
import winterImage from "../../assets/images/beauty/color-analysis-result-winter.jpg";
import eyeColorsImage from "../../assets/images/beauty/color-analysis-eye-colors.jpg";
import hairColorsImage from "../../assets/images/beauty/color-analysis-hair-colors.jpg";
import jewelryImage from "../../assets/images/beauty/color-analysis-jewelry.jpg";
import colorGroupsImage from "../../assets/images/beauty/color-analysis-color-groups.jpg";
import skinUndertoneImage from "../../assets/images/beauty/color-analysis-skin-undertone.jpg";
import veinsImage from "../../assets/images/beauty/color-analysis-veins.jpg";
import contrastImage from "../../assets/images/beauty/color-analysis-contrast.jpg";
import neutralsImage from "../../assets/images/beauty/color-analysis-neutrals.jpg";

import { QUESTIONS, RESULT_DATA, calculateColorSeason } from "../../utils/color-analysis";
import MainLayout from "../../layouts/main-layout";
import StaticPageNav from "../organisms/static-page-nav";
import MinimalFooter from "../organisms/minimal-footer";

const resultImages: Record<string, string> = {
    spring: springImage,
    summer: summerImage,
    autumn: autumnImage,
    winter: winterImage,
};

const questionImages: Record<string, { src: string; alt: string }> = {
    undertone: {
        src: skinUndertoneImage,
        alt: "Porównanie trzech odcieni skóry: ciepłego, chłodnego i neutralnego",
    },
    veins: {
        src: veinsImage,
        alt: "Porównanie zielonych, niebiesko-fioletowych i mieszanych żył na nadgarstku",
    },
    contrast: {
        src: contrastImage,
        alt: "Przykłady rosnącego kontrastu między skórą, włosami i oczami na czterech portretach",
    },
    "neutral-test": {
        src: neutralsImage,
        alt: "Porównanie czterech zestawów neutralnych tkanin przy twarzy: ciepłych, chłodnych, ziemistych i kontrastowych",
    },
    "best-colors": {
        src: colorGroupsImage,
        alt: "Porównanie czterech grup kolorów: świeżych, pastelowych, ziemistych i kontrastowych",
    },
    jewelry: {
        src: jewelryImage,
        alt: "Porównanie złotej biżuterii na ciepłej tkaninie i srebrnej biżuterii na chłodnej tkaninie",
    },
    hair: {
        src: hairColorsImage,
        alt: "Przykłady naturalnych kolorów włosów: złocistego blondu, jasnego brązu, rudego i ciemnego brązu",
    },
    eyes: {
        src: eyeColorsImage,
        alt: "Przykłady czterech kolorów oczu: szaroniebieskiego, zielonego, bursztynowego i intensywnie niebieskiego",
    },
};

const seasonOrder = ["spring", "summer", "autumn", "winter"];
const resultData: Record<string, any> = RESULT_DATA;

const ColorAnalysis: React.FC = () => {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState<string[]>([]);
    const [result, setResult] = useState<any>(null);
    const question = QUESTIONS[step];
    const questionImage = question ? questionImages[question.id] : undefined;
    const progress = result ? 100 : Math.round(((step + 1) / QUESTIONS.length) * 100);

    const scrollToQuiz = () => {
        if (typeof window !== "undefined") {
            window.requestAnimationFrame(() => document.getElementById("analiza")?.scrollIntoView({ behavior: "smooth", block: "start" }));
        }
    };

    const chooseAnswer = (answerId: string) => {
        const nextAnswers = answers.slice(0, step);
        nextAnswers[step] = answerId;
        setAnswers(nextAnswers);
        if (step === QUESTIONS.length - 1) {
            setResult(calculateColorSeason(nextAnswers));
        } else {
            setStep(step + 1);
        }
        scrollToQuiz();
    };

    const goBack = () => {
        if (step === 0) return;
        setResult(null);
        setStep(step - 1);
        scrollToQuiz();
    };

    const restart = () => {
        setAnswers([]);
        setResult(null);
        setStep(0);
        scrollToQuiz();
    };

    return (
        <MainLayout
            className={analysisPage}
            HeaderComponent={null}
            footer={false}
            SEOProps={{ title: "Analiza kolorystyczna online | Fashion Fairy" }}
        >
            <StaticPageNav menuId="color-analysis-mobile-menu" />
            <main className={pageShell}>
                <section className={intro} aria-labelledby="analysis-title">
                    <div className={introCopy}>
                        <p className={eyebrow}>Bezpłatny quiz Fashion Fairy</p>
                        <h1 className={introTitle} id="analysis-title">Poznaj swój<br /><em>typ kolorystyczny</em></h1>
                        <p className={introLead}>Odpowiedz na 8 prostych pytań o skórę, włosy, oczy i kolory, w których wyglądasz najlepiej. Otrzymasz dopasowaną paletę oraz praktyczne wskazówki.</p>
                        <ul className={introBadges}>
                            <li className={introBadge}><strong>8</strong><span>krótkich<br />pytań</span></li>
                            <li className={introBadge}><strong>2–3</strong><span>minuty</span></li>
                            <li className={introBadge}><strong>0</strong><span>zdjęć i<br />logowania</span></li>
                        </ul>
                    </div>
                    <div className={introArt}><img src={heroImage} alt="Kobieta podczas analizy kolorystycznej na tle wachlarza barwnych tkanin" /></div>
                </section>

                <section className={quizSection} id="analiza" aria-labelledby={result ? "result-title" : "question-title"}>
                    <div className={progressMeta} aria-live="polite">
                        <span>{result ? "Analiza zakończona" : `Pytanie ${step + 1} z ${QUESTIONS.length}`}</span>
                        <strong>{progress}%</strong>
                    </div>
                    <div className={progressTrack} aria-hidden="true"><span className={progressFill} style={{ width: `${progress}%` }} /></div>

                    {!result && question && (
                        <div className={questionCard}>
                            <p className={questionNumber}>Krok {step + 1}</p>
                            <h2 className={questionTitle} id="question-title">{question.title}</h2>
                            <p className={questionHint}>{question.hint}</p>
                            {questionImage && (
                                <figure className={questionVisual}>
                                    <img src={questionImage.src} alt={questionImage.alt} />
                                </figure>
                            )}
                            <div className={answersGrid} role="group" aria-label={question.title}>
                                {question.answers.map((answer: any) => (
                                    <button
                                        className={`${answerButton} ${answers[step] === answer.id ? answerSelected : ""}`}
                                        type="button"
                                        key={answer.id}
                                        onClick={() => chooseAnswer(answer.id)}
                                        aria-pressed={answers[step] === answer.id}
                                    >
                                        <span>{answer.label}</span><b aria-hidden="true">→</b>
                                    </button>
                                ))}
                            </div>
                            <div className={quizNav}>
                                <button className={backButton} type="button" onClick={goBack} disabled={step === 0}>← Poprzednie pytanie</button>
                                <p className={privacyNote}>Odpowiedzi pozostają wyłącznie w tej przeglądarce.</p>
                            </div>
                        </div>
                    )}

                    {result && (
                        <div className={resultSection} aria-live="polite">
                            <header className={resultHeader}>
                                <div>
                                    <p className={resultEyebrow}>Twój wynik</p>
                                    <h2 className={resultTitle} id="result-title">Twój typ urody to <em>{result.name}</em></h2>
                                    <p>Najbliższy typ uzupełniający: <strong>{resultData[result.runnerUp].name}</strong></p>
                                </div>
                                <div className={confidence}><strong>{result.confidence}%</strong><span>spójności<br />odpowiedzi</span></div>
                            </header>

                            <div className={resultGrid}>
                                <div className={resultPortrait}><img src={resultImages[result.season]} alt={`Przykład typu urody ${result.name}`} /></div>
                                <div className={resultSummary}>
                                    <p className={resultEyebrow}>Charakterystyka</p>
                                    <h3>{result.tone}</h3>
                                    <p>{result.summary}</p>
                                    <h4>Najlepsze kolory</h4>
                                    <div className={resultPalette} aria-label={`Paleta typu ${result.name}`}>
                                        {result.colors.map((color: string) => <span className={swatch} style={{ backgroundColor: color }} key={color} />)}
                                    </div>
                                </div>
                            </div>

                            <div className={resultLists}>
                                <article className={resultList}><h3>Kolory neutralne</h3><ul>{result.neutrals.map((item: string) => <li key={item}>✓ {item}</li>)}</ul></article>
                                <article className={`${resultList} ${avoidList}`}><h3>Na razie unikaj przy twarzy</h3><ul>{result.avoid.map((item: string) => <li key={item}>× {item}</li>)}</ul></article>
                            </div>

                            <details className={scoreDetails}>
                                <summary>Zobacz rozkład punktów</summary>
                                {seasonOrder.map((season) => {
                                    const maxScore = Math.max(...seasonOrder.map((key) => result.scores[key]), 1);
                                    return <div className={scoreRow} key={season}><span>{resultData[season].name}</span><div><i className={scoreBar} style={{ width: `${Math.round((result.scores[season] / maxScore) * 100)}%` }} /></div><strong>{result.scores[season]}</strong></div>;
                                })}
                            </details>

                            <div className={resultActions}>
                                <Link className={primaryAction} to="/generator/wyczaruj-stylizacje">Wyczaruj stylizację →</Link>
                                <Link className={secondaryAction} to="/typ-urody/">Poznaj wszystkie typy</Link>
                                <button className={restartButton} type="button" onClick={restart}>Zacznij analizę od nowa</button>
                            </div>
                            <p className={disclaimer}>Wynik jest praktyczną analizą orientacyjną opartą na Twoich odpowiedziach. Farbowane włosy, makijaż, oświetlenie i odpowiedzi mieszane mogą wpływać na rezultat.</p>
                        </div>
                    )}
                </section>
            </main>
            <MinimalFooter />
        </MainLayout>
    );
};

export const query = graphql`
    query($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) { edges { node { ns data language } } }
    }
`;

export default ColorAnalysis;
